import { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Multiselect,
  Select,
  SpaceBetween,
} from "@cloudscape-design/components";
import type { MultiselectProps, SelectProps } from "@cloudscape-design/components";
import type { ParamFilter, TableColumn, TableWidget } from "./viewContract";
import { type SchemaContentType } from "./schemaApi";

/**
 * Reusable, SELF-CONTAINED configuration form for ONE `table` widget, extracted from the
 * former single-table builder. It is a CONTROLLED component: the parent owns the
 * `TableWidget` value and receives a replacement on every edit via `onChange`. All the
 * former single-table features are preserved, now per-card and repeatable, with the
 * cascading resets scoped to THIS widget value:
 *  - content-type Select,
 *  - Relations multiselect (to-one only),
 *  - Fields multiselect (main scalars + one-hop relation fields as
 *    `<mainShort>.<relation>.<field>`, incl. `documentId` offered as a field),
 *  - Columns list (header Input + field Select + Advanced modal with Context Link +
 *    the small advanced-config summary line),
 *  - Page size Input,
 *  - Filterable fields multiselect (main-collection scalars only; excludes relation
 *    paths and `documentId`).
 *
 * Changing the content-type clears this widget's relations/fields/column mappings/
 * filterable fields; removing a relation drops its dotted fields + column mappings;
 * deselecting a field drops column mappings + now-invalid filterable fields.
 */

/** Short name of a content type — leading segment of a stored relation field path. */
function shortNameFor(ct: SchemaContentType): string {
  return ct.apiName ?? ct.uid.split(".").pop() ?? ct.uid;
}

/** Valid filterable field names: plain main-collection scalars (no relation paths, no documentId). */
function validFilterFieldSet(selectedFields: string[], mainShort: string): Set<string> {
  const prefix = `${mainShort}.`;
  return new Set(selectedFields.filter((f) => f !== "documentId" && !f.startsWith(prefix)));
}

/**
 * Valid PARAM-FILTER field names: main-collection scalars PLUS "documentId" (so id-based
 * lookups work), EXCLUDING relation-path fields (`<mainShort>.*`). Unlike
 * `validFilterFieldSet`, this one INCLUDES "documentId".
 */
function validParamFilterFieldSet(selectedFields: string[], mainShort: string): Set<string> {
  const prefix = `${mainShort}.`;
  const set = new Set(selectedFields.filter((f) => !f.startsWith(prefix)));
  set.add("documentId");
  return set;
}

export default function TableWidgetConfig({
  value,
  schema,
  paramNames,
  onChange,
}: {
  value: TableWidget;
  schema: SchemaContentType[];
  /** View-level param names (from PageEditorPage) offered in each param-filter's param Select. */
  paramNames: string[];
  onChange: (next: TableWidget) => void;
}) {
  // "Advanced" per-column modal: which column index is open (null = closed) and the draft
  // contextLink being edited (committed on confirm, discarded on dismiss). Local UI state.
  const [advancedColumnIndex, setAdvancedColumnIndex] = useState<number | null>(null);
  const [advancedDraft, setAdvancedDraft] = useState<string>("");

  const collection = value.collection;
  const selectedRelations = value.relations;
  const selectedFields = value.selected;
  const columns = value.columns;
  const pageSize = value.pageSize;
  const filterableFields = value.filterableFields ?? [];
  const paramFilters = value.paramFilters ?? [];

  const currentCt = useMemo(
    () => schema.find((c) => c.uid === collection) ?? null,
    [schema, collection]
  );

  const collectionOptions: SelectProps.Option[] = useMemo(
    () => schema.map((c) => ({ label: c.displayName, value: c.uid })),
    [schema]
  );

  // To-one relations (one hop). Many-side and unknown relationTypes are excluded.
  const relationOptions: MultiselectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    return currentCt.relations
      .filter((r) => r.relationType === "oneToOne" || r.relationType === "manyToOne")
      .map((r) => ({ label: r.name, value: r.name, description: `-> ${r.target ?? "?"}` }));
  }, [currentCt]);

  // UNION of the main type's own scalar fields and, per chosen relation, that relation
  // target's scalar fields emitted as dotted paths "<mainShort>.<relation>.<field>".
  const fieldOptions: MultiselectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    const mainShort = shortNameFor(currentCt);
    const own: MultiselectProps.Option[] = currentCt.fields.map((f) => ({
      label: f.name,
      value: f.name,
    }));
    const related: MultiselectProps.Option[] = [];
    for (const relName of selectedRelations) {
      const rel = currentCt.relations.find((r) => r.name === relName);
      if (!rel || !rel.target) continue;
      const targetCt = schema.find((c) => c.uid === rel.target);
      if (!targetCt) continue;
      for (const f of targetCt.fields) {
        const path = `${mainShort}.${relName}.${f.name}`;
        related.push({ label: path, value: path, description: `via ${relName}` });
      }
    }
    return [...own, ...related];
  }, [currentCt, schema, selectedRelations]);

  const filterableFieldOptions: MultiselectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    const prefix = `${shortNameFor(currentCt)}.`;
    return selectedFields
      .filter((f) => f !== "documentId" && !f.startsWith(prefix))
      .map((f) => ({ label: f, value: f }));
  }, [currentCt, selectedFields]);

  const selectedFieldOptions: SelectProps.Option[] = useMemo(
    () => selectedFields.map((f) => ({ label: f, value: f })),
    [selectedFields]
  );

  // Param-filter FIELD options: main-collection scalars (exclude relation paths) PLUS
  // "documentId". Unlike filterableFields, documentId IS offered here for id-based lookup.
  const paramFilterFieldOptions: SelectProps.Option[] = useMemo(() => {
    if (!currentCt) return [];
    const prefix = `${shortNameFor(currentCt)}.`;
    const scalars = selectedFields.filter((f) => f !== "documentId" && !f.startsWith(prefix));
    return [{ label: "documentId", value: "documentId" }, ...scalars.map((f) => ({ label: f, value: f }))];
  }, [currentCt, selectedFields]);

  // Param-filter PARAM options: the view's defined param names (from PageEditorPage).
  const paramOptions: SelectProps.Option[] = useMemo(
    () => paramNames.map((p) => ({ label: p, value: p })),
    [paramNames]
  );

  // --- Mutators: every edit produces a fresh TableWidget via onChange -----------------

  const onCollectionChange = (uid: string) => {
    // Clear relations, fields and column mappings; drop all filterable fields.
    onChange({
      ...value,
      collection: uid,
      relations: [],
      selected: [],
      columns: columns.map((c) => ({ ...c, field: "" })),
      filterableFields: [],
      paramFilters: [],
    });
  };

  const onSelectedRelationsChange = (next: string[]) => {
    const removed = selectedRelations.filter((r) => !next.includes(r));
    if (removed.length === 0) {
      onChange({ ...value, relations: next });
      return;
    }
    const mainShort = currentCt ? shortNameFor(currentCt) : "";
    const belongsToRemoved = (field: string) =>
      removed.some((rel) => field.startsWith(`${mainShort}.${rel}.`));
    const nextSelected = selectedFields.filter((f) => !belongsToRemoved(f));
    onChange({
      ...value,
      relations: next,
      selected: nextSelected,
      columns: columns.map((c) => (c.field && belongsToRemoved(c.field) ? { ...c, field: "" } : c)),
      filterableFields: filterableFields, // relation removal never affects main scalars
    });
  };

  const onSelectedFieldsChange = (next: string[]) => {
    const mainShort = currentCt ? shortNameFor(currentCt) : "";
    const valid = validFilterFieldSet(next, mainShort);
    const validParam = validParamFilterFieldSet(next, mainShort);
    onChange({
      ...value,
      selected: next,
      columns: columns.map((c) => (c.field && !next.includes(c.field) ? { ...c, field: "" } : c)),
      filterableFields: filterableFields.filter((f) => valid.has(f)),
      // Drop param-filter rows whose field is no longer a valid option (not a current
      // main-collection scalar and not "documentId").
      paramFilters: paramFilters.filter((pf) => validParam.has(pf.field)),
    });
  };

  const setColumns = (updater: (prev: TableColumn[]) => TableColumn[]) =>
    onChange({ ...value, columns: updater(columns) });

  const addColumn = () => setColumns((prev) => [...prev, { header: "", field: "" }]);
  const removeColumn = (index: number) => setColumns((prev) => prev.filter((_, i) => i !== index));
  const setColumnHeader = (index: number, header: string) =>
    setColumns((prev) => prev.map((c, i) => (i === index ? { ...c, header } : c)));
  const setColumnField = (index: number, field: string) =>
    setColumns((prev) => prev.map((c, i) => (i === index ? { ...c, field } : c)));

  const openAdvanced = (index: number) => {
    setAdvancedDraft(columns[index]?.contextLink ?? "");
    setAdvancedColumnIndex(index);
  };
  const cancelAdvanced = () => setAdvancedColumnIndex(null);
  const confirmAdvanced = () => {
    if (advancedColumnIndex === null) return;
    const idx = advancedColumnIndex;
    const trimmed = advancedDraft.trim();
    setColumns((prev) =>
      prev.map((c, i) => (i === idx ? { ...c, contextLink: trimmed === "" ? undefined : advancedDraft } : c))
    );
    setAdvancedColumnIndex(null);
  };

  const onPageSizeChange = (raw: string) => {
    const v = raw.trim();
    if (v === "") {
      onChange({ ...value, pageSize: undefined });
      return;
    }
    const n = Number(v);
    if (!Number.isNaN(n)) onChange({ ...value, pageSize: n });
  };

  const onFilterableFieldsChange = (next: string[]) =>
    onChange({ ...value, filterableFields: next });

  // --- Param-filter row mutators (each row = a field + a param binding) ---------------
  const setParamFilters = (updater: (prev: ParamFilter[]) => ParamFilter[]) =>
    onChange({ ...value, paramFilters: updater(paramFilters) });
  const addParamFilter = () => setParamFilters((prev) => [...prev, { field: "", param: "" }]);
  const removeParamFilter = (index: number) =>
    setParamFilters((prev) => prev.filter((_, i) => i !== index));
  const setParamFilterField = (index: number, field: string) =>
    setParamFilters((prev) => prev.map((pf, i) => (i === index ? { ...pf, field } : pf)));
  const setParamFilterParam = (index: number, param: string) =>
    setParamFilters((prev) => prev.map((pf, i) => (i === index ? { ...pf, param } : pf)));

  return (
    <SpaceBetween size="l">
      <Box variant="h4">Fields</Box>
      <FormField
        label="Content type"
        description="Select the single content type this table draws data from."
      >
        <Select
          selectedOption={
            collection
              ? collectionOptions.find((o) => o.value === collection) ?? {
                  label: collection,
                  value: collection,
                }
              : null
          }
          options={collectionOptions}
          onChange={({ detail }) => onCollectionChange(detail.selectedOption.value ?? "")}
          placeholder="Choose a content type"
          empty="No content types available"
        />
      </FormField>

      {collection && (
        <FormField
          label="Relations"
          description="Optionally pick to-one relations (one hop) to expose their fields. Only oneToOne/manyToOne relations are available."
        >
          <Multiselect
            selectedOptions={selectedRelations.map(
              (r) => relationOptions.find((o) => o.value === r) ?? { label: r, value: r }
            )}
            options={relationOptions}
            onChange={({ detail }) =>
              onSelectedRelationsChange(
                detail.selectedOptions.map((o) => o.value).filter((v): v is string => Boolean(v))
              )
            }
            placeholder="Choose relations (optional)"
            empty="This content type has no to-one relations"
          />
        </FormField>
      )}

      {collection && (
        <FormField
          label="Fields"
          description="Choose which fields are available to columns and filters. Includes this content type's own fields plus the fields of any chosen relations (shown as dotted paths)."
        >
          <Multiselect
            selectedOptions={selectedFields.map(
              (f) => fieldOptions.find((o) => o.value === f) ?? { label: f, value: f }
            )}
            options={fieldOptions}
            onChange={({ detail }) =>
              onSelectedFieldsChange(
                detail.selectedOptions.map((o) => o.value).filter((v): v is string => Boolean(v))
              )
            }
            placeholder="Choose fields"
            empty="This content type has no selectable fields"
          />
        </FormField>
      )}

      <Box variant="h4">Table</Box>
      <FormField
        label="Columns"
        description="Add, remove and order the columns. Each column has a static header and maps to one selected field."
      >
        <SpaceBetween size="s">
          {columns.length === 0 && <Box color="text-body-secondary">No columns yet.</Box>}
          {columns.map((col, index) => {
            const advancedSummary: { label: string; value: string }[] = [];
            if (col.contextLink && col.contextLink.trim() !== "") {
              advancedSummary.push({ label: "Context Link", value: col.contextLink });
            }
            return (
              <div key={`col-${index}`}>
                <SpaceBetween direction="horizontal" size="xs">
                  <Input
                    value={col.header}
                    placeholder="Column header"
                    onChange={({ detail }) => setColumnHeader(index, detail.value)}
                  />
                  <Select
                    selectedOption={
                      col.field
                        ? selectedFieldOptions.find((o) => o.value === col.field) ?? {
                            label: col.field,
                            value: col.field,
                          }
                        : null
                    }
                    options={selectedFieldOptions}
                    onChange={({ detail }) => setColumnField(index, detail.selectedOption.value ?? "")}
                    placeholder="Map to field"
                    empty="Select fields first"
                    disabled={selectedFields.length === 0}
                  />
                  <Button
                    onClick={() => openAdvanced(index)}
                    ariaLabel={`Advanced settings for column ${index + 1}`}
                  >
                    Advanced
                  </Button>
                  <Button
                    iconName="close"
                    variant="icon"
                    ariaLabel={`Remove column ${index + 1}`}
                    onClick={() => removeColumn(index)}
                  />
                </SpaceBetween>
                {advancedSummary.length > 0 && (
                  <Box fontSize="body-s" color="text-body-secondary">
                    {advancedSummary.map((entry) => (
                      <div key={entry.label}>
                        {entry.label}: {entry.value}
                      </div>
                    ))}
                  </Box>
                )}
              </div>
            );
          })}
          <Button iconName="add-plus" onClick={addColumn}>
            Add column
          </Button>
        </SpaceBetween>
      </FormField>

      <FormField
        label="Page size"
        description="Number of rows fetched per page from the server. Leave empty to use the default of 10."
      >
        <Input
          type="number"
          value={pageSize === undefined ? "" : String(pageSize)}
          placeholder="10"
          onChange={({ detail }) => onPageSizeChange(detail.value)}
        />
      </FormField>

      <FormField
        label="Filterable fields"
        description="Choose which of this content type's own scalar fields the end viewer can filter on. The viewer sets the actual values at runtime via text inputs shown above the table; nothing is applied by default."
      >
        <Multiselect
          selectedOptions={filterableFields.map(
            (f) => filterableFieldOptions.find((o) => o.value === f) ?? { label: f, value: f }
          )}
          options={filterableFieldOptions}
          onChange={({ detail }) =>
            onFilterableFieldsChange(
              detail.selectedOptions.map((o) => o.value).filter((v): v is string => Boolean(v))
            )
          }
          placeholder="Choose filterable fields (optional)"
          empty="No filterable fields available"
          disabled={filterableFieldOptions.length === 0}
        />
      </FormField>

      <FormField
        label="Parameter filters"
        description="Bind a table field to a view-level URL parameter. When the page is visited with that parameter present, the table is filtered to exact matches (operator is always equals). Applied invisibly — no filter control is shown. If the parameter is absent in the URL, the binding is ignored."
      >
        <SpaceBetween size="s">
          {!collection && (
            <Box color="text-body-secondary">Select a content type first.</Box>
          )}
          {collection && paramNames.length === 0 && (
            <Box color="text-body-secondary">
              No view parameters defined yet. Add a parameter in the Parameters section above
              to bind a filter to it.
            </Box>
          )}
          {collection && paramNames.length > 0 && paramFilters.length === 0 && (
            <Box color="text-body-secondary">No parameter filters yet.</Box>
          )}
          {collection &&
            paramFilters.map((pf, index) => (
              <div key={`pf-${index}`}>
                <SpaceBetween direction="horizontal" size="xs">
                  <Select
                    selectedOption={
                      pf.field
                        ? paramFilterFieldOptions.find((o) => o.value === pf.field) ?? {
                            label: pf.field,
                            value: pf.field,
                          }
                        : null
                    }
                    options={paramFilterFieldOptions}
                    onChange={({ detail }) => setParamFilterField(index, detail.selectedOption.value ?? "")}
                    placeholder="Map to field"
                    empty="Select fields first"
                  />
                  <Select
                    selectedOption={
                      pf.param
                        ? paramOptions.find((o) => o.value === pf.param) ?? {
                            label: pf.param,
                            value: pf.param,
                          }
                        : null
                    }
                    options={paramOptions}
                    onChange={({ detail }) => setParamFilterParam(index, detail.selectedOption.value ?? "")}
                    placeholder="Bind to parameter"
                    empty="No parameters defined"
                    disabled={paramNames.length === 0}
                  />
                  <Button
                    iconName="close"
                    variant="icon"
                    ariaLabel={`Remove parameter filter ${index + 1}`}
                    onClick={() => removeParamFilter(index)}
                  />
                </SpaceBetween>
              </div>
            ))}
          {collection && (
            <Button
              iconName="add-plus"
              onClick={addParamFilter}
              disabled={paramNames.length === 0}
            >
              Add parameter filter
            </Button>
          )}
        </SpaceBetween>
      </FormField>

      <Modal
        visible={advancedColumnIndex !== null}
        onDismiss={cancelAdvanced}
        header="Column advanced settings"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={cancelAdvanced}>
                Cancel
              </Button>
              <Button variant="primary" onClick={confirmAdvanced}>
                Save
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <FormField
          label="Context Link"
          description={
            selectedFields.length > 0
              ? `Optional href template. Use {field} placeholders substituted per row. Available fields: ${selectedFields
                  .map((f) => `{${f}}`)
                  .join(", ")}`
              : "Optional href template. Select fields above to see the available {placeholder} tokens."
          }
        >
          <Input
            value={advancedDraft}
            placeholder="/stacks/{account.documentId}"
            onChange={({ detail }) => setAdvancedDraft(detail.value)}
          />
        </FormField>
      </Modal>
    </SpaceBetween>
  );
}

/** A fresh, empty table widget used when the author adds a new table card/tab. */
export function emptyTableWidget(): TableWidget {
  return { type: "table", collection: "", relations: [], selected: [], columns: [] };
}
